import {
  Component,
  computed,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  resource,
  signal,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  ApiClient,
  AttachmentDto,
  AttachmentDtoKind,
  CollaboratorDto,
  FileResponse,
  ForwardAttachmentsToIcareDto,
  ForwardSelectedAttachmentToIcareDto,
  MessageDto,
  MessageWithAttachmentsDto,
  MessageWithAttachmentsDtoStatus,
  MessageWithContextDto,
  ReplyMessageDto,
  ReplyMessageDtoChannel,
  ReplyMessageDtoDirection,
  UpdateMessageCollaboratorDto,
  UpdateMessageStatusDto
} from '../../services/api.clients.generated';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { IcareDocumentTypeOption, IcareService } from '../../services/icare.service';
import { EcabButtonComponent, EcabInputComponent, EcabSelectComponent } from '@ecab/ecab-ui';
import { NotificationService } from '../../notifications/notification.service';
import { MarkdownEditorComponent } from '../../shared/markdown-editor/markdown-editor.component';
import { ConversationHeaderComponent } from './conversation-header/conversation-header.component';
import { MessageCardComponent } from './message-card/message-card.component';
import { TransferDialogComponent } from './transfer-dialog/transfer-dialog.component';
import { ContextChipData } from '../../shared/context-chip/context-chip.component';
import { getContactAvatarColor, getContactInitials } from '../contact-display.utils';
import { getAttachmentIcon, getAttachmentLabel } from '../attachment-display.utils';
import { getQuestionTypeColor } from '../question-type-color';

type AttachmentPreviewKind = 'image' | 'pdf' | 'unavailable';

interface DayGroup {
  key: string;
  label: string;
  messages: MessageWithAttachmentsDto[];
}

interface AttachmentPreviewState {
  attachmentId?: number;
  kind: AttachmentPreviewKind;
  title: string;
  subtitle?: string;
  sourceUrl?: string;
  safeSourceUrl?: SafeResourceUrl;
}

@Component({
  selector: 'app-message-details',
  imports: [
    TranslatePipe,
    MatIcon,
    ReactiveFormsModule,
    FormsModule,
    EcabButtonComponent,
    EcabInputComponent,
    EcabSelectComponent,
    MarkdownEditorComponent,
    ConversationHeaderComponent,
    MessageCardComponent,
    TransferDialogComponent
  ],
  templateUrl: './message-details.component.html',
  styleUrl: './message-details.component.scss'
})
export class MessageDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  private readonly messageState = signal<MessageWithContextDto | undefined>(undefined);

  @Input()
  set message(value: MessageWithContextDto | undefined) {
    this.messageState.set(value);
  }

  get message(): MessageWithContextDto | undefined {
    return this.messageState();
  }

  replyForm: FormGroup;

  private activeAttachmentPreviewUrl: string | null = null;

  protected readonly attachmentKinds = AttachmentDtoKind;
  protected readonly attachmentPreview = signal<AttachmentPreviewState | null>(null);
  protected readonly selectedAttachmentIds = signal<number[]>([]);
  protected readonly selectedIcareDocumentTypes = signal<Record<number, string>>({});
  protected readonly selectedIcareComments = signal<Record<number, string>>({});
  protected readonly forwardingSelectedAttachments = signal(false);
  protected readonly documentTypesResource = resource<IcareDocumentTypeOption[], undefined>({
    loader: async () => await lastValueFrom(this.icareService.getDocumentTypes())
  });
  protected readonly icareDocumentTypeOptions = computed(() => this.documentTypesResource.value() ?? []);
  protected readonly icareBlockedAttachmentIds = computed(() => {
    const blocked = new Set<number>();
    for (const message of this.sortedChatMessages()) {
      if (!this.isNegativeCaseId(message.caseId)) {
        continue;
      }
      for (const attachment of message.attachments ?? []) {
        if (attachment.id !== undefined && attachment.id !== null) {
          blocked.add(attachment.id);
        }
      }
    }
    return blocked;
  });

  /** The message the inline reply panel is currently composing a reply to, if any. */
  protected readonly replyTarget = signal<MessageWithAttachmentsDto | null>(null);
  /** The message whose "Transférer" (qualification) dialog is currently open, if any. */
  protected readonly transferMessage = signal<MessageWithAttachmentsDto | null>(null);
  /** The message whose "Envoyer sur ICARE" dialog is currently open, if any. */
  protected readonly icareDialogMessage = signal<MessageWithAttachmentsDto | null>(null);
  /** Selected, still-eligible attachments of the message whose ICARE dialog is open. */
  protected readonly icareDialogAttachments = computed<AttachmentDto[]>(() => {
    const message = this.icareDialogMessage();
    if (!message) {
      return [];
    }

    const selectedIds = new Set(this.selectedAttachmentIds());
    const blockedAttachmentIds = this.icareBlockedAttachmentIds();
    return (message.attachments ?? []).filter((attachment): attachment is AttachmentDto =>
      attachment.kind === AttachmentDtoKind.FILE && attachment.id !== undefined && attachment.id !== null
      && !attachment.icareDocumentId && !blockedAttachmentIds.has(attachment.id) && selectedIds.has(attachment.id));
  });
  protected readonly icareQualifiedCount = computed(() => {
    const documentTypes = this.selectedIcareDocumentTypes();
    return this.icareDialogAttachments().filter(attachment =>
      attachment.id !== undefined && attachment.id !== null && !!documentTypes[attachment.id]).length;
  });
  protected readonly canConfirmIcareForward = computed(() => {
    const attachments = this.icareDialogAttachments();
    if (!attachments.length || this.documentTypesResource.isLoading() || this.forwardingSelectedAttachments()) {
      return false;
    }

    const documentTypes = this.selectedIcareDocumentTypes();
    return attachments.every(attachment =>
      attachment.id !== undefined && attachment.id !== null && !!documentTypes[attachment.id]);
  });

  readonly getChatMessages = signal<MessageWithAttachmentsDto[]>([]);

  /** Conversation title: the message subject, translated when it is a known ContactSubject key. */
  readonly headerSubject = computed<string | undefined>(() => {
    const raw = this.messageState()?.message?.subject;
    if (!raw) {
      return raw;
    }
    const path = 'message.contactSubject.' + raw;
    const label = this.translateService.instant(path);
    // ngx-translate returns the path itself when the key is missing -> fall back to the raw value.
    return label === path ? raw : label;
  });

  /** Context chips shown in the header, mirroring the displayed message. */
  readonly headerChips = computed<ContextChipData[]>(() => {
    const msg = this.messageState()?.message;
    return [
      this.buildPersonChip(msg),
      this.buildBuildingChip(msg),
      this.buildCaseChip(msg),
      this.buildCategoryChip(msg)
    ];
  });

  readonly collaboratorsResource = resource<CollaboratorDto[], undefined>({
    loader: async () => await lastValueFrom(this.messageService.getCollaborators())
  });
  readonly collaboratorOptions = computed(() => this.collaboratorsResource.value() ?? []);

  readonly sortedChatMessages = computed(() =>
    [...this.getChatMessages()].sort((a, b) => this.getMessageTimestamp(a) - this.getMessageTimestamp(b))
  );

  /** Messages grouped by calendar day, with a localized day label for the separator. */
  readonly groupedMessages = computed<DayGroup[]>(() => {
    const groups: DayGroup[] = [];
    for (const message of this.sortedChatMessages()) {
      const date = message.date instanceof Date ? message.date : new Date(this.getMessageTimestamp(message));
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.key === key) {
        lastGroup.messages.push(message);
      } else {
        groups.push({ key, label: this.formatDayLabel(date), messages: [message] });
      }
    }
    return groups;
  });

  constructor(
    private readonly messageService: ApiClient,
    private readonly icareService: IcareService,
    private readonly route: ActivatedRoute,
    public readonly router: Router,
    protected readonly location: Location,
    private readonly fb: FormBuilder,
    private readonly sanitizer: DomSanitizer,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService
  ) {
    this.replyForm = this.fb.group({
      message: ['', Validators.required],
      language: [this.translateService.currentLang || 'fr', Validators.required]
    });
  }

  ngOnInit() {
    const messageId = this.route.snapshot.paramMap.get('messageId');
    if (messageId && !isNaN(+messageId)) {
      this.fetchMessageById(Number(messageId));
    }

    this.route.paramMap.subscribe(params => {
      const id = params.get('messageId');
      if (id && !isNaN(+id) && (!this.message || this.message['id'] !== +id)) {
        this.message = undefined;
        this.fetchMessageById(Number(id));
      }
    });
  }

  ngOnDestroy() {
    this.closeAttachmentPreview();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.attachmentPreview()) {
      this.closeAttachmentPreview();
    }
  }

  private scrollToBottom(): void {
    try {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
    } catch (err) { }
  }

  private scheduleScrollToBottom(): void {
    setTimeout(() => this.scrollToBottom());
  }

  // fetch data
  fetchMessageById(messageId: number) {
    firstValueFrom(this.messageService.getInternalMessageById(messageId)).then(
      (message: MessageWithContextDto) => {
        if (!message.message) {
          return;
        }

        const chatMessages = [message.message, ...(message.sameContextMessages ?? [])]
          .filter((item): item is MessageWithAttachmentsDto => !!item);

        this.message = message;
        this.getChatMessages.set(chatMessages);
        this.scheduleScrollToBottom();
      },
      error => {
        this.notificationService.error(
          this.notificationService.errorMessage(error, this.translateService.instant('message.details.feedback.loadError'))
        );
      }
    );
  }

  private refreshCurrentMessage(): void {
    const currentMessageId = this.message?.message?.id;
    if (currentMessageId) {
      this.fetchMessageById(currentMessageId);
    }
  }

  // attachments
  protected async openAttachment(attachment: AttachmentDto, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (attachment.kind === AttachmentDtoKind.LINK) {
      this.openAttachmentLink(attachment);
      return;
    }

    if (attachment.icareDocumentUrl) {
      window.open(attachment.icareDocumentUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    await this.openAttachmentPreview(attachment);
  }

  protected closeAttachmentPreview(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.releaseAttachmentPreviewUrl();
    this.attachmentPreview.set(null);
  }

  protected toggleAttachmentSelection(attachment: AttachmentDto, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (attachment.kind !== AttachmentDtoKind.FILE || attachment.id === undefined || attachment.id === null
      || this.isAttachmentIcareBlocked(attachment)) {
      return;
    }

    const attachmentId = attachment.id;
    this.selectedAttachmentIds.update(current =>
      current.includes(attachmentId)
        ? current.filter(id => id !== attachmentId)
        : [...current, attachmentId]);
  }

  /** Selects all still-eligible attachments of a message, or clears them if all are already selected. */
  protected toggleSelectAllForMessage(message: MessageWithAttachmentsDto): void {
    const blocked = this.icareBlockedAttachmentIds();
    const eligibleIds = (message.attachments ?? [])
      .filter(attachment => attachment.kind === AttachmentDtoKind.FILE && attachment.id != null
        && !attachment.icareDocumentId && !blocked.has(attachment.id))
      .map(attachment => attachment.id)
      .filter((id): id is number => id !== undefined && id !== null);

    if (!eligibleIds.length) {
      return;
    }

    const selected = new Set(this.selectedAttachmentIds());
    const allSelected = eligibleIds.every(id => selected.has(id));
    this.selectedAttachmentIds.update(current => {
      const next = new Set(current);
      for (const id of eligibleIds) {
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return [...next];
    });
  }

  protected isAttachmentSelected(attachment: AttachmentDto): boolean {
    return attachment.id !== undefined && attachment.id !== null && this.selectedAttachmentIds().includes(attachment.id);
  }

  protected isAttachmentIcareBlocked(attachment: AttachmentDto): boolean {
    return attachment.id !== undefined && attachment.id !== null && this.icareBlockedAttachmentIds().has(attachment.id);
  }

  /**
   * A negative ICARE case/incident id means the case is not in a step that accepts documents,
   * so its attachments cannot be forwarded to ICARE.
   */
  private isNegativeCaseId(caseId: string | null | undefined): boolean {
    if (caseId === undefined || caseId === null || caseId.trim() === '') {
      return false;
    }

    const parsed = Number(caseId);
    return Number.isFinite(parsed) && parsed < 0;
  }

  protected onIcareDocumentTypeChange(attachmentId: number | undefined, value: string | null): void {
    if (!attachmentId) {
      return;
    }

    this.selectedIcareDocumentTypes.update(current => {
      if (!value) {
        const { [attachmentId]: _, ...rest } = current;
        return rest;
      }

      return {
        ...current,
        [attachmentId]: value
      };
    });
  }

  protected getSelectedIcareDocumentType(attachmentId: number | undefined): string | null {
    if (!attachmentId) {
      return null;
    }

    return this.selectedIcareDocumentTypes()[attachmentId] ?? null;
  }

  protected onIcareCommentChange(attachmentId: number | undefined, value: string): void {
    if (!attachmentId) {
      return;
    }

    this.selectedIcareComments.update(current => {
      if (!value.trim()) {
        const { [attachmentId]: _, ...rest } = current;
        return rest;
      }

      return {
        ...current,
        [attachmentId]: value
      };
    });
  }

  protected getSelectedIcareComment(attachmentId: number | undefined): string {
    if (!attachmentId) {
      return '';
    }

    return this.selectedIcareComments()[attachmentId] ?? '';
  }

  /** Opens the "Envoyer sur ICARE" dialog for a message's currently selected attachments. */
  protected openIcareDialog(message: MessageWithAttachmentsDto): void {
    this.icareDialogMessage.set(message);
  }

  protected closeIcareDialog(): void {
    this.icareDialogMessage.set(null);
  }

  protected getAttachmentIcon(attachment: AttachmentDto): string {
    return getAttachmentIcon(attachment);
  }

  protected getAttachmentLabel(attachment: { filename?: string; storageId?: string; linkUrl?: string }): string {
    return getAttachmentLabel(attachment, this.translateService.instant('common.unknown'));
  }

  private openAttachmentLink(attachment: AttachmentDto): void {
    const linkUrl = attachment.linkUrl?.trim();

    if (!linkUrl) {
      this.showUnavailableAttachmentPreview(attachment);
      return;
    }

    window.open(linkUrl, '_blank', 'noopener,noreferrer');
  }

  private async openAttachmentPreview(attachment: AttachmentDto): Promise<void> {
    if (!attachment.id) {
      this.showUnavailableAttachmentPreview(attachment);
      return;
    }

    try {
      const response = await firstValueFrom(this.messageService.downloadAttachment(attachment.id));
      this.showAttachmentPreviewFromResponse(attachment, response);
    } catch {
      this.showUnavailableAttachmentPreview(attachment);
    }
  }

  private showAttachmentPreviewFromResponse(attachment: AttachmentDto, response: FileResponse): void {
    const previewBlob = response.data;
    const contentType = previewBlob.type || attachment.contentType || '';

    if (!this.isAttachmentPreviewable(contentType) || typeof URL.createObjectURL !== 'function') {
      this.showUnavailableAttachmentPreview(attachment);
      return;
    }

    this.releaseAttachmentPreviewUrl();

    const objectUrl = URL.createObjectURL(previewBlob);
    this.activeAttachmentPreviewUrl = objectUrl;
    const title = this.getAttachmentLabel(attachment);

    if (contentType.toLowerCase().startsWith('image/')) {
      this.attachmentPreview.set({
        attachmentId: attachment.id,
        kind: 'image',
        title,
        subtitle: this.translateService.instant('message.details.preview.imageType'),
        sourceUrl: objectUrl
      });
      return;
    }

    this.attachmentPreview.set({
      attachmentId: attachment.id,
      kind: 'pdf',
      title,
      subtitle: this.translateService.instant('message.details.preview.pdfType'),
      sourceUrl: objectUrl,
      safeSourceUrl: this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl)
    });
  }

  private showUnavailableAttachmentPreview(attachment: AttachmentDto): void {
    this.releaseAttachmentPreviewUrl();
    this.attachmentPreview.set({
      attachmentId: attachment.id,
      kind: 'unavailable',
      title: this.getAttachmentLabel(attachment),
      subtitle: this.translateService.instant('message.details.preview.unavailableType')
    });
  }

  protected async confirmIcareForward(): Promise<void> {
    const selectedAttachments = this.icareDialogAttachments();
    if (!selectedAttachments.length) {
      return;
    }

    const missingDocumentType = selectedAttachments.some(attachment => {
      const attachmentId = attachment.id;
      return attachmentId === undefined || attachmentId === null || !this.selectedIcareDocumentTypes()[attachmentId];
    });

    if (missingDocumentType) {
      this.notificationService.warning(
        this.translateService.instant('message.details.feedback.attachmentIcareDocumentTypeRequired')
      );
      return;
    }

    this.forwardingSelectedAttachments.set(true);

    try {
      const results = await firstValueFrom(
        this.messageService.forwardAttachmentsToIcare(
          new ForwardAttachmentsToIcareDto({
            attachments: selectedAttachments.map(attachment => new ForwardSelectedAttachmentToIcareDto({
              attachmentId: attachment.id!,
              documentType: this.selectedIcareDocumentTypes()[attachment.id!] ?? '',
              comment: this.selectedIcareComments()[attachment.id!]?.trim() ?? ''
            }))
          })
        )
      );

      this.handleBulkIcareForwardResults(results);
    } catch (error) {
      this.notificationService.error(
        this.notificationService.errorMessage(
          error,
          this.translateService.instant('message.details.feedback.attachmentIcareUploadError')
        )
      );
    } finally {
      this.forwardingSelectedAttachments.set(false);
    }
  }

  private handleBulkIcareForwardResults(
    results: { attachmentId?: number; success?: boolean; errorMessage?: string | null; documentId?: string | null; icareDocumentUrl?: string | null }[]
  ): void {
    const failedAttachmentIds = results
      .filter(result => !result.success && result.attachmentId !== undefined && result.attachmentId !== null)
      .map(result => result.attachmentId!)
      .filter((attachmentId, index, values) => values.indexOf(attachmentId) === index);
    const successCount = results.filter(result => result.success).length;
    const failureCount = results.length - successCount;

    this.selectedAttachmentIds.set(failedAttachmentIds);

    const savedResults = results.filter(r => r.success && r.attachmentId != null && r.documentId);
    if (savedResults.length > 0) {
      this.getChatMessages.update(messages =>
        messages.map(msg => new MessageWithAttachmentsDto({
          ...msg,
          attachments: (msg.attachments ?? []).map(att => {
            const result = savedResults.find(r => r.attachmentId === att.id);
            return result ? new AttachmentDto({ ...att, icareDocumentId: result.documentId!, icareDocumentUrl: result.icareDocumentUrl!, storageId: null as unknown as string }) : att;
          })
        }))
      );
    }

    if (failureCount === 0) {
      this.closeIcareDialog();
      this.notificationService.success(
        this.translateService.instant('message.details.feedback.attachmentIcareBulkUploadSuccess', {
          count: successCount
        })
      );
      return;
    }

    this.notificationService.warning(
      this.translateService.instant('message.details.feedback.attachmentIcareBulkUploadPartial', {
        successCount,
        failureCount
      })
    );
  }

  private isAttachmentPreviewable(contentType: string): boolean {
    const normalizedContentType = contentType.toLowerCase();
    return normalizedContentType === 'application/pdf' || normalizedContentType.startsWith('image/');
  }

  private releaseAttachmentPreviewUrl(): void {
    if (this.activeAttachmentPreviewUrl && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(this.activeAttachmentPreviewUrl);
    }

    this.activeAttachmentPreviewUrl = null;
  }

  // per-message assignment + status (action row)
  protected onAssignCollaborator(event: { message: MessageWithAttachmentsDto; collaborator: CollaboratorDto | null }): void {
    const { message, collaborator } = event;
    if (!message.id) {
      return;
    }

    const body = new UpdateMessageCollaboratorDto();
    body.assignedCollaboratorId = collaborator?.id ?? '';
    body.assignedCollaboratorEmail = collaborator?.email ?? '';

    lastValueFrom(this.messageService.updateMessageCollaborator(message.id, body)).then(
      () => {
        this.refreshCurrentMessage();
        this.notificationService.success(
          this.translateService.instant(collaborator
            ? 'message.details.feedback.collaboratorUpdated'
            : 'message.details.feedback.collaboratorCleared')
        );
      },
      error => {
        this.notificationService.error(
          this.notificationService.errorMessage(error, this.translateService.instant('message.details.feedback.collaboratorUpdateError'))
        );
      }
    );
  }

  protected openTransferDialog(message: MessageWithAttachmentsDto): void {
    this.transferMessage.set(message);
  }

  protected closeTransferDialog(): void {
    this.transferMessage.set(null);
  }

  protected onContextSaved(): void {
    this.closeTransferDialog();
    this.refreshCurrentMessage();
  }

  protected onChangeStatus(event: { message: MessageWithAttachmentsDto; status: MessageWithAttachmentsDtoStatus }): void {
    const { message, status } = event;
    if (!message.id || message.status === status) {
      return;
    }

    const body = new UpdateMessageStatusDto();
    body.status = status as unknown as UpdateMessageStatusDto['status'];

    lastValueFrom(this.messageService.updateMessageStatus(message.id, body)).then(
      () => {
        this.refreshCurrentMessage();
        this.notificationService.success(this.translateService.instant('message.details.feedback.statusUpdated'));
      },
      error => {
        this.notificationService.error(
          this.notificationService.errorMessage(error, this.translateService.instant('message.details.feedback.statusUpdateError'))
        );
      }
    );
  }

  // reply
  protected openReply(message: MessageWithAttachmentsDto): void {
    this.replyForm.reset({ message: '', language: this.translateService.currentLang || 'fr' });
    this.replyTarget.set(message);
  }

  protected closeReply(): void {
    this.replyTarget.set(null);
    this.replyForm.reset({ message: '', language: this.translateService.currentLang || 'fr' });
  }

  sendReply() {
    const target = this.replyTarget();
    if (this.replyForm.valid && target?.id) {
      const { message, language } = this.replyForm.value;
      const reply = new ReplyMessageDto();
      // Subject is inherited from the parent message on the backend; no longer sent per-reply.
      reply.content = message;
      (reply as any)['language'] = language;
      reply.channel = ReplyMessageDtoChannel.PORTAL;
      reply.direction = ReplyMessageDtoDirection.OUT;
      lastValueFrom(this.messageService.replyInternalMessage(target.id, reply)).then(
        (msg) => {
          this.closeReply();
          this.notificationService.success(this.translateService.instant('message.details.feedback.replySent'));
          const createdMessageId = msg.message?.id;
          if (createdMessageId) {
            this.router.navigateByUrl(`/messages/${createdMessageId}`);
          }
        },
        error => {
          this.notificationService.error(
            this.notificationService.errorMessage(error, this.translateService.instant('message.details.feedback.replySendError'))
          );
        }
      );
    }
  }

  protected getLanguageOptions() {
    return [
      { value: 'fr', label: 'FR' },
      { value: 'de', label: 'DE' }
    ];
  }

  // header context chips
  getContactDisplay(msg: MessageDto | MessageWithAttachmentsDto): string {
    if (msg.firstname && msg.lastname) {
      return `${msg.firstname} ${msg.lastname}`;
    }
    return msg.companyName || msg.email || this.translateService.instant('common.unknown');
  }

  private buildPersonChip(msg: MessageWithAttachmentsDto | undefined): ContextChipData {
    if (msg?.personId) {
      return {
        label: this.getContactDisplay(msg),
        filled: true,
        avatarText: getContactInitials(msg),
        avatarColor: getContactAvatarColor(msg)
      };
    }
    return {
      label: this.translateService.instant('message.context.person'),
      empty: true,
      icon: 'person'
    };
  }

  private buildBuildingChip(msg: MessageWithAttachmentsDto | undefined): ContextChipData {
    const address = msg?.buildingAddress;
    return address
      ? { label: address, icon: 'home' }
      : { label: this.translateService.instant('message.context.building'), empty: true, icon: 'home' };
  }

  private buildCaseChip(msg: MessageWithAttachmentsDto | undefined): ContextChipData {
    const caseLabel = msg ? this.getCaseLabel(msg) : null;
    return caseLabel
      ? { label: caseLabel, icon: 'folder' }
      : { label: this.translateService.instant('message.context.case'), empty: true, icon: 'folder' };
  }

  private buildCategoryChip(msg: MessageWithAttachmentsDto | undefined): ContextChipData {
    const questionType = msg?.questionType;
    if (questionType) {
      return {
        label: this.translateService.instant('message.questionType.' + questionType),
        dotColor: getQuestionTypeColor(questionType)
      };
    }
    return {
      label: this.translateService.instant('message.context.questionType'),
      empty: true,
      icon: 'category'
    };
  }

  private getCaseLabel(msg: MessageWithAttachmentsDto): string | null {
    if (msg.caseSource?.toUpperCase() === 'ICARE' && msg.caseTitle) {
      return msg.caseTitle;
    }
    if (msg.caseType && msg.caseTitle) {
      return `${this.translateService.instant('caseType.' + msg.caseType.toLowerCase())} - ${msg.caseTitle}`;
    }
    if (msg.caseType) {
      return this.translateService.instant('caseType.' + msg.caseType.toLowerCase());
    }
    if (msg.caseTitle) {
      return msg.caseTitle;
    }
    return null;
  }

  // utils
  private getMessageTimestamp(message: MessageWithAttachmentsDto): number {
    const rawDate = message.date;

    if (rawDate instanceof Date) {
      return rawDate.getTime();
    }

    if (typeof rawDate === 'string') {
      const timestamp = Date.parse(rawDate);
      return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    return 0;
  }

  /** Localized day-separator label, e.g. "16 juin 2026" (fr) / "16. Juni 2026" (de). */
  private formatDayLabel(date: Date): string {
    const language = this.translateService.currentLang || this.translateService.defaultLang || 'fr';
    return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  }
}
